import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';

import API, { Types } from '@/modules/API';
import Utils from '@/modules/Utils';
import PropUtils from '@/modules/PropUtils';
import vuex from '..';
import get from 'lodash/get';

export const base_columns = [
  'class_name',
  'dmodified_ts',
  'id',
  'meta.category',
  'meta.commission',
  'meta.device',
  'meta.location',
  'name',
  'power',
  'project_id',
  'sys___active',
  'sys___active__modified',
  'sys___connected',
  'sys___online'
];

export const fixture_columns = [
  'parent_id', // lora - tondo
  'device.hardwareid',
  'device.schedule',
  'meta.imsi.imsi',
  'meta.type.type', // asset - tondo
  'meta.part_number.part_number',
  'meta.install_location',
  'meta.schedule_curve',
  'smodified_ts',
  'rssi',
  'disconnect_reason',
  'power_consumption',
  'low_power_alert',
  'version_nrf52840'
  // 'meta.stream', // video
];

const cabinet_columns = [
  'created_ts',
  'device.hardwareid',
  'device.schedule',
  'device.version',
  'meta.imsi.imsi',
  'meta.hardware_ids',
  'meta.devices_schedule',
  'services.config',
  'dashboard.connection_display',
  'smodified_ts',
  'rssi',
  'circuits',
  'device_connected_percent'
];

const sensors_columns = [
  'water_mark',
  'water_height_trigger',
  'lsi1',
  'lsi2'
];

const ITEMS = 5000;
export const REPORTING_QUERY_VERSION = 2;
const mqtt_map_streams = ['power', 'sys___active', 'sys___connected', 'lsi1', 'lsi2', 'water_mark', 'water_height_trigger'];
// const cabinets_class_names = `'Power_Meter_HummingBoard', 'Power_Meter_Hummingboard', 'sbc_cabinet', 'sbc_cabinet_extensions', 'Extension_Card', 'sbc_cabinet_pm_135', 'virtual_cabinet'`;
// const sensors_class_names = `'NB_BLE_nRF52V2_flood_sensor', 'Sensor', 'Water_Meter', 'rule_board'`;

const setDeviceCommonFields = (device, default_location) => {
  try {
    device['meta.device'] = device['meta.device'] || {};
    device['meta.commission'] = PropUtils.parseMetaCommissionProperty(device['meta.commission'] || {});
    [device['meta.location'], device.has_valid_location, device.has_default_location] = PropUtils.parseMetaLocationProperty(device['meta.location'] || {}, default_location);
    device['meta.category'] = device['meta.category'] || { category: ''};
  } catch (e) {
    console.log('%c' + 'error:' + e + '\ndevice:' + device.id, 'background: #F74242; color: white');
  }
};

const setCabinetFields = (device) => {
  try {
    device['meta.hardware_ids'] = device['meta.hardware_ids'] || { eth0: '' };
    device['meta.devices_schedule'] = device['meta.devices_schedule'] || {};
    device['meta.imsi.imsi'] = device['meta.imsi.imsi'] || '';
    device['device.version'] = device['device.version'] || {};
    device['device.hardwareid'] = device['device.hardwareid'] || { id: '' };
    device['device.schedule'] = device['device.schedule'] || (Utils.hasCabinetClassWithNewFormat(device['device.version']) ? { schedules: {}, days_schedules: {}} : { schedule: [] });
    device['services.config'] = device['services.config'] || {};
    device['dashboard.connection_display'] = device['dashboard.connection_display'] || {
      enable_outputs: 1,
      enable_inputs: 1,
      display_outputs: 1,
      display_inputs: 1
    };

  } catch (e) {
    console.log('%c' + 'error:' + e + '\ndevice:' + device.id, 'background: #F74242; color: white');
  }
};

const setFixtureFields = (device) => {
  try {
    device['meta.imsi.imsi'] = device['meta.imsi.imsi'] || '';
    device['meta.type.type'] = device['meta.type.type'] || '';
    device['meta.part_number.part_number'] = device['meta.part_number.part_number'] || '';
    device['meta.install_location'] = device['meta.install_location'] || {
      gps_location: { lat: '', lng: '' },
      app_location: { lat: '', lng: '' },
      gis_location: { lat: '', lng: '' },
      manual_location: { lat: '', lng: '' }
    };
    device['meta.schedule_curve'] = device['meta.schedule_curve'] || {};
    device['device.version'] = device['device.version'] || {};
    device['device.hardwareid'] = device['device.hardwareid'] || { id: '' };
    device['device.schedule'] = device['device.schedule'] || { schedule: [] };
  } catch (e) {
    console.log('%c' + 'error:' + e + '\ndevice:' + device.id, 'background: #F74242; color: white');
  }
};

const setRestFields = (device) => {
  try{
    device['device.schedule'] = device['device.schedule'] || (Utils.hasCabinetClassWithNewFormat(device['device.version']) ? { schedules: {}, days_schedules: {}} : { schedule: [] });
  } catch (e) {
    console.log('%c' + 'error:' + e + '\ndevice:' + device.id, 'background: #F74242; color: white');
  }
};

@Module({ namespaced: true })
export default class Reports extends VuexModule {
  reportsList: any[] = [];
  devicesCount: number = -1;
  cabinetsCount: number = -1;
  pagesCount: number = 0;
  companyId: string = '';
  projectId: string = '';
  allProjectsData: any = {};
  devicesChangedFromMqtt = new Map();

  @Action
  async updateDeviceMode(project_id, device_id, mode) {
    return API.post(
      '',
      `${Types.PROJECTS}/${project_id}/${Types.DEVICES}/${device_id}/streams/sys___active/evt/`,
      { content: mode },
      {},
      'v4'
    );
  }

  @Action
  resetReports() {
    vuex.commit('Reports/clearReports');
  }

  @Mutation
  setDeviceField(data) {
    const device = this.reportsList.find((device) => device.id === data.device_id);
    if (device && device[data.field] !== data.value) {
      device[data.field] = !isNaN(data.value) ? +data.value : data.value;
      if (mqtt_map_streams.includes(data.field)){
        vuex.commit('Reports/setDeviceChangedFromMqtt', device);
      }
    }
  }

  @Mutation
  setDeviceChangedFromMqtt(device) {
      this.devicesChangedFromMqtt = new Map(this.devicesChangedFromMqtt).set(device.id, device);

  }

  @Mutation
  removeDeviceChangedFromMqtt(device_id) {
    this.devicesChangedFromMqtt.delete(device_id);
  }

  @Mutation
  clearReports() {
    this.reportsList = [];
  }

  @Mutation
  setDevicesReportsList(data: any) {
    const meta_device_options = new Map();

    if (data.type === 'cabinet') {
      data.devices.forEach((device) => {
        setDeviceCommonFields(device, data.default_location);
        setCabinetFields(device);
        
        if (device['meta.commission']?.commissioned){
          const meta_device_fields = Object.entries(device['meta.device']).filter(([field_name, value]) => !field_name.includes('backup'));
          meta_device_fields.forEach(([field_name, value]) => {
            meta_device_options.has(field_name) 
              ? meta_device_options.get(field_name).push(value)
              : meta_device_options.set(field_name, [value]);
          });
        }
      });
      vuex.commit('Props/setCabinetMetaDeviceOptions', meta_device_options);
    }else if (data.type === 'fixture') {
      data.devices.forEach((device) => {
        setDeviceCommonFields(device, data.default_location);
        setFixtureFields(device);

        if (Utils.hasTondoClass(device['meta.category'].category)) {
          const meta_device_fields = Object.entries(device['meta.device']).filter(([field_name, value]) => !field_name.includes('backup'));
          meta_device_fields.forEach(([field_name, value]) => {
            meta_device_options.has(field_name) 
              ? meta_device_options.get(field_name).push(value)
              : meta_device_options.set(field_name, [value]);
          });
        }
      });
      vuex.commit('Props/setFixtureMetaDeviceOptions', meta_device_options);
    }else {
      // rest
      data.devices.forEach((device) => {
        setDeviceCommonFields(device, data.default_location);
        setRestFields(device);
      });
    }

    vuex.commit('Reports/setDevicesReports', data.devices);
  }

  @Mutation
  setDevicesReports(data: any) {
    this.reportsList.push(...data);
  }

  @Mutation
  setDevicesCount(count) {
    this.devicesCount = this.devicesCount === -1 ? count : this.devicesCount + count;
  }

  @Mutation
  setCabinetsCount() {
    this.cabinetsCount = vuex.getters['Reports/commissionedCabinets'].length;
  }

  @Mutation
  clearCabinetsCount() {
    this.cabinetsCount = -1;
  }

  @Mutation
  clearDevicesCount(){
    this.devicesCount = -1;
  }

  @Mutation
  setPagesCount(count) {
    this.pagesCount = count;
  }

  @Mutation
  setCompanyId(id) {
    this.companyId = id;
  }

  @Mutation
  setProjectId(id) {
    this.projectId = id;
  }

  @Mutation
  setAllProjectsData(allProjectsData) {
    this.allProjectsData = allProjectsData;
  }

  @Mutation
  deleteDeviceFromList(deviceId) {
    this.reportsList = this.reportsList.filter((device) => device.id !== deviceId);
    vuex.commit('Reports/setDevicesCount', -1);
  }

  @Mutation
  addDeviceToList(device) {
    this.reportsList.push(device);
    vuex.commit('Reports/setDevicesCount', 1);
  }

  @Mutation
  editDeviceField(field_data) {
    const device_to_update = this.reportsList.find((device) => device.id === field_data.device_id);
    if (device_to_update) {
      if (Array.isArray(field_data.name)) {
        Utils.setNestedValue(device_to_update, field_data.name, field_data.value);
      } else {
        device_to_update[field_data.name] = field_data.value;
      }
    }
  }

  @Action
  async loadReports(data) {
    const user = localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'));

    vuex.commit('Props/defineProjectDefaultLocation', data.id);
    vuex.commit('Global/setProjectSunTime');
    vuex.commit('Reports/setCompanyId', data.company || (user && user.company));
    vuex.commit('Reports/setProjectId', data.id);

    if (data.id === 'allProjects') {
      vuex.commit('Reports/setAllProjectsData', data);
    }

    try {
      if (data.id === 'allProjects') {
        await vuex.dispatch('Reports/getPagesCountAllProjects', data);
        await vuex.dispatch('Reports/getAllDevicesAllProjects', data);
      } else {
        await vuex.dispatch('Reports/getPagesCount');
        await vuex.dispatch('Reports/getAllDevices');
      }
    } catch (e) {
      vuex.commit('Errors/setError', get(e, 'response.data.error.message', e));
      vuex.commit('Errors/setShowError', true);
      console.error(e);
    }
  }


  @Action
  async getPagesCount() {
    const request_data = {
      type: 'count',
      columns: ['count(*)'],
      where: [
        "meta.commission.commissioned=true"
      ],
      page_number: 0,
      version: REPORTING_QUERY_VERSION
    };
    try {
      await vuex.dispatch('Reports/loadReportsPage', request_data);
      const count = (this.state as any).devicesCount;
      if (count) {
        let pages_count = 0;
        pages_count = count > ITEMS ? Math.floor(count / ITEMS) + 1 : 1;
        vuex.commit('Reports/setPagesCount', pages_count);
      }
    } catch (e) {
      throw e;
    }
  }

  @Action
  async getAllDevices() {
    const company_id = (this.state as any).companyId;
    try {
      vuex.dispatch('Reports/fetchCabinets', company_id);
      vuex.dispatch('Reports/fetchFixtures', company_id);
      vuex.dispatch('Reports/fetchRest', company_id);
    } catch (e) {
      throw e;
    }
  }

  @Action
  fetchRest(company_id){
    vuex.dispatch('Reports/loadReportsPage', {
      type: 'rest',
      columns: base_columns.concat(sensors_columns),
      page_number: 0,
      default_location: (vuex.state as any).Props.project_default_location,
      where: [
        "meta.category.category!='HUB'",
        "meta.category.category!='FIXTURE'",
        "meta.commission.commissioned=true"
      ],
      version: REPORTING_QUERY_VERSION
    });
  }

  @Action
  fetchCabinets(company_id){
    vuex.dispatch('Reports/loadReportsPage', {
      type: 'cabinet',
      columns: base_columns.concat(cabinet_columns),
      page_number: 0,
      default_location: (vuex.state as any).Props.project_default_location,
      where: ["meta.category.category='HUB'"],
      version: REPORTING_QUERY_VERSION
    }).then(() => {
      vuex.commit('Reports/setCabinetsCount');
      const uncommissioned_cabinets_count = vuex.getters['Reports/cabinets'].length - vuex.getters['Reports/commissionedCabinets'].length;
      vuex.commit('Reports/setDevicesCount', uncommissioned_cabinets_count);
    });
  }

  @Action
  async fetchFixtures(company_id){
    const pages = (this.state as any).pagesCount;
    const project_id = (this.state as any).projectId;
    
    await Promise.all(
      [...Array(pages).keys()].map(async (page_number) => {
        await vuex.dispatch('Reports/loadReportsPage', {
          type: 'fixture',
          columns: base_columns.concat(fixture_columns),
          page_number,
          default_location: (vuex.state as any).Props.project_default_location,
          where: ["meta.category.category='FIXTURE'", "meta.commission.commissioned=true"],
          version: REPORTING_QUERY_VERSION
        });
      })
    );
  }

  @Action
  async loadReportsPage(request_data) {
    const project_id = (this.state as any).projectId;
    const company_id = (this.state as any).companyId;
    if (project_id === 'allProjects') return;
    
    const body = {
      columns: request_data.columns,
      page: request_data.page_number,
      items: ITEMS,
      version: REPORTING_QUERY_VERSION
    };

    if (request_data.order_by) body['order_by'] = request_data.order_by;
    if (request_data.where) body['where'] = request_data.where;
    try {
      const result = await API.post(
        Types.COMPANIES,
        `/${company_id}/projects/${project_id}/reporting_query/`,
        body,
        {}
      );
      if (result && result.length) {
        if (request_data.type === 'count') {
          vuex.commit('Reports/setDevicesCount', result[0].count);
        } else {
          const devices = result;
          vuex.commit('Reports/setDevicesReportsList', {
            type: request_data.type,
            devices,
            default_location: request_data.default_location
          });
        }
      }
    } catch (e) {
      throw e;
    }
  }

  
  @Action
  async getPagesCountAllProjects(allProjectsData) {
    const request_data = {
      type: 'count',
      columns: ['count(*)'],
      where: [
        "meta.commission.commissioned=true"
      ],
      page_number: 0,
      version: REPORTING_QUERY_VERSION
    };
    try {
      await vuex.dispatch('Reports/loadReportsPageAllProjects', {
        request_data,
        allProjectsData
      });
      const count = (this.state as any).devicesCount;
      if (count) {
        let pages_count = 0;
        pages_count = count > ITEMS ? Math.floor(count / ITEMS) + 1 : 1;
        vuex.commit('Reports/setPagesCount', pages_count);
      }
    } catch (e) {
      throw e;
    }
  }

  @Action
  async getAllDevicesAllProjects(data) {
    const company_id = data.companiesAllProjects[0];

    try {
      vuex.dispatch('Reports/fetchCabinetsAllProjects', {company_id, data});
      vuex.dispatch('Reports/fetchFixturesAllProjects', {company_id, data});
      vuex.dispatch('Reports/fetchRestAllProjects', {company_id, data});
    } catch (e) {
      throw e;
    }
  }

  @Action
  fetchCabinetsAllProjects({company_id, data}){
    const request_data = {
      type: 'cabinet',
      columns: base_columns.concat(cabinet_columns),
      page_number: 0,
      default_location: (vuex.state as any).Props.project_default_location,
      order_by: 'id',
      where: ["meta.category.category='HUB'"],
      version: REPORTING_QUERY_VERSION
    };
    vuex.dispatch('Reports/loadReportsPageAllProjects', {
      request_data,
      allProjectsData: data
    }).then(() => {
      vuex.commit('Reports/setCabinetsCount');
      const uncommissioned_cabinets_count = vuex.getters['Reports/cabinets'].length - vuex.getters['Reports/commissionedCabinets'].length;
      vuex.commit('Reports/setDevicesCount', uncommissioned_cabinets_count);
    });
  }

  @Action
  fetchRestAllProjects({company_id, data}){
    const request_data = {
      type: 'rest',
      columns: base_columns.concat(sensors_columns),
      page_number: 0,
      default_location: (vuex.state as any).Props.project_default_location,
      where: [
        "meta.category.category!='HUB'",
        "meta.category.category!='FIXTURE'",
        "meta.commission.commissioned=true"
      ],
      version: REPORTING_QUERY_VERSION
    };
    vuex.dispatch('Reports/loadReportsPageAllProjects', {
      request_data,
      allProjectsData: data
    });
  }

  @Action
  async fetchFixturesAllProjects({company_id, data}){
    const pages = (this.state as any).pagesCount;
    await Promise.all(
      [...Array(pages).keys()].map(async (page_number) => {
        const request_data = {
          type: 'fixture',
          columns: base_columns.concat(fixture_columns),
          page_number,
          default_location: (vuex.state as any).Props.project_default_location,
          where: ["meta.category.category='FIXTURE'", "meta.commission.commissioned=true"],
          version: REPORTING_QUERY_VERSION
        };
        await vuex.dispatch('Reports/loadReportsPageAllProjects', {
          request_data,
          allProjectsData: data
        });
      })
    );
  }

  @Action
  async loadReportsPageAllProjects({ request_data, allProjectsData }) {
    if (!allProjectsData || allProjectsData === undefined || !allProjectsData.projects) return;
    for await (const item of allProjectsData.projects) {
      const project_id = item.id;
      const company_id = item.company;

      const body = {
        columns: request_data.columns,
        page: request_data.page_number,
        items: ITEMS,
        version: REPORTING_QUERY_VERSION
      };

      if (request_data.order_by) body['order_by'] = request_data.order_by;
      if (request_data.where) body['where'] = request_data.where;
      try {
        const result = await API.post(
          Types.COMPANIES,
          company_id + '/projects/' + project_id + '/reporting_query/',
          body,
          {}
        );
        if (result && result.length) {
          if (body.page === 0 && result[0].count) {
            vuex.commit('Reports/setDevicesCount', result[0].count);
          } else {
            const devices = result;
            vuex.commit('Reports/setDevicesReportsList', {
              type: request_data.type,
              devices,
              default_location: request_data.default_location
            });
          }
        }
      } catch (e) {
        console.log(`error: ${JSON.stringify(e)}`);
        throw e;
      }
    }
  }

  get devicesList_map() {
    const devices_map = new Map();
    this.reportsList.forEach((device) => devices_map.set(device.id, device));
    return devices_map;
  }

  get cabinets() {
    return this.reportsList.filter((device) => Utils.hasCabinetClass(device['meta.category']?.category));
  }

  get commissionedFixtures() {
    return this.reportsList.filter((device) => Utils.hasTondoClass(device['meta.category']?.category));
  }

  get commissionedFixtures_map() {
    const devices_map = new Map();
    vuex.getters['Reports/commissionedFixtures'].forEach((device) => {
      devices_map.set(device.id, device);
    });
    return devices_map;
  }

  get cabinets_fixtures() {
    const devices_by_cabinet = new Map();
    vuex.getters['Reports/commissionedCabinets'].forEach((cabinet) => {
      const cabinet_id = cabinet['meta.device'].cabinet_id;
      const related_devices = vuex.getters['Reports/commissionedFixtures'].filter((device) => device['meta.device'].cabinet_id === cabinet_id);
      devices_by_cabinet.set(cabinet.id, related_devices);
    });

    return devices_by_cabinet;
  }

  get ruleBoards() {
    return this.reportsList.filter((device) => Utils.hasRuleBoardClass(device.class_name));
  }

  get commissionedDevices() {
    return this.reportsList.filter((device) => device['meta.commission']?.commissioned);
  }

  get commissioned_map() {
    const devices_map = new Map();
    vuex.getters['Reports/commissionedDevices'].forEach((device) => {
      devices_map.set(device.id, device);
    });
    return devices_map;
  }

  get commissionedWithLocation() {
    return this.reportsList.filter(
      (device) => 
        device['meta.commission']?.commissioned &&
        device.has_valid_location
    );
  }

  get commissionedCabinets() {
    return vuex.getters['Reports/cabinets'].filter(
      (device) => device['meta.commission']?.commissioned
    );
  }

  get commissionedCabinets_map() {
    const devices_map = new Map();
    vuex.getters['Reports/commissionedCabinets'].forEach((device) => {
      devices_map.set(device.id, device);
    });
    return devices_map;
  }

  get commissionedMotionSensor() {
    return this.reportsList.filter(
      (device) =>
        Utils.hasMotionSensorClass(device.class_name)
    );
  }

  get commissionedFloodSensor() {
    return this.reportsList.filter(
      (device) => Utils.hasFloodSensorClass(device['meta.category']?.category)
    );
  }

  get commissionedBarrier(){
    return this.reportsList.filter(
      (device) =>
        Utils.hasBarrierClass(device['meta.category']?.category)
    );
  }

  get commissionedWaterMeter() {
    return this.reportsList.filter(
      (device) =>
        Utils.hasWaterMeterClass(device.class_name)
    );
  }

  get commissionedWeatherStation() {
    return this.reportsList.filter(
      (device) =>
        Utils.hasWeatherStationClass(device.class_name)
    );
  }

  get commissionedRadar() {
    return this.reportsList.filter(
      (device) =>
        Utils.hasRadarClass(device.class_name)
    );
  }

  get commissionedCamera() {
    return this.reportsList.filter(
      (device) =>
        Utils.hasCameraClass(device['meta.category'].category)
    );
  }

  get motion_sensor_and_meta_device_map() {
    const devices_map = new Map();
    vuex.getters['Reports/commissionedMotionSensor'].forEach((device) => {
      const metaDevice = device['meta.device'];
      devices_map.set(device.id, [device, metaDevice]);
    });
    return devices_map;
  }

  get assetTypeList() {
    const assetTypeList = new Set();

    vuex.getters['Reports/commissionedWithLocation'].forEach((device) => {
      if (device['meta.type.type']) assetTypeList.add(device['meta.type.type']);
    });

    return [...assetTypeList];
  }

  get devicesTypeList() {
    const devicesTypeList = new Set();

    vuex.getters['Reports/commissionedWithLocation'].forEach((device) => {
      if (device['meta.category'].category) devicesTypeList.add(device['meta.category'].category);
    });

    return [...devicesTypeList];
  }

  get deviceParentList() {
    const parentList = this.reportsList.reduce((r, a) => {
      r[a.parent_id] = r[a.parent_id] || [];
      r[a.parent_id].push(a);
      return r;
    }, Object.create(null));
    return parentList;
  }
}
